/**
 * API Client pour les signatures électroniques
 * Base URL: /signatures (le client api ajoute déjà /api/v1)
 */

import api from '@/utils/api';
import type {
  Signature,
  CreateSignatureInput,
  SignatureListResult,
  SignatureStats,
  ProofPdfResult,
  SignatureFilters,
} from '../types/signatures.types';

export interface CreateSignatureFromBase64Input {
  signedEntityType: 'Invoice' | 'Quote' | 'WorkOrder';
  signedEntityId: string;
  clientName: string;
  clientEmail: string;
  signatureImageBase64: string;
  timestamp?: string;
}

export const signaturesAPI = {
  /**
   * Créer une nouvelle signature électronique
   */
  create: async (data: CreateSignatureInput): Promise<Signature> => {
    return api.post<Signature>('/signatures', data);
  },

  /**
   * Créer une signature depuis une image base64 (canvas)
   * 🔒 Upload automatique vers MinIO + génération PDF preuve via BullMQ
   */
  createFromBase64: async (data: CreateSignatureFromBase64Input): Promise<Signature> => {
    return api.post<Signature>('/signatures/from-base64', data);
  },

  /**
   * Obtenir une signature par ID
   */
  getById: async (id: string): Promise<Signature> => {
    return api.get<Signature>(`/signatures/${id}`);
  },

  /**
   * Obtenir la signature d'une entité
   */
  getByEntity: async (
    entityType: string,
    entityId: string
  ): Promise<Signature> => {
    return api.get<Signature>(
      `/signatures/entity/${entityType}/${entityId}`
    );
  },

  /**
   * Obtenir toutes les signatures d'une entité (historique)
   */
  getAllByEntity: async (
    entityType: string,
    entityId: string
  ): Promise<Signature[]> => {
    return api.get<Signature[]>(
      `/signatures/entity/${entityType}/${entityId}/all`
    );
  },

  /**
   * Lister les signatures avec filtres
   */
  list: async (filters?: SignatureFilters): Promise<SignatureListResult> => {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    return api.get<SignatureListResult>(
      `/signatures?${params.toString()}`
    );
  },

  /**
   * Générer le PDF de preuve de signature
   * 🔒 Stockage hiérarchique: signatures/{tenantId}/{companyId}/{year}/{month}/proofs/
   */
  generateProofPdf: async (signatureId: string): Promise<ProofPdfResult> => {
    return api.post<ProofPdfResult>(
      `/signatures/${signatureId}/proof-pdf`
    );
  },

  /**
   * Obtenir les statistiques de signature
   */
  getStats: async (): Promise<SignatureStats> => {
    return api.get<SignatureStats>('/signatures/stats');
  },

  /**
   * Supprimer une signature (ADMIN seulement - preuve légale)
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/signatures/${id}`);
  },
};

// Re-export for backwards compatibility
export default signaturesAPI;
